import React, { useState } from 'react';
import { useApp } from '../contexts/AppContext';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from './ui/select';
import { Switch } from './ui/switch';
import { Badge } from './ui/badge';
import { 
  Dialog, 
  DialogContent, 
  DialogDescription, 
  DialogHeader, 
  DialogTitle, 
  DialogTrigger 
} from './ui/dialog';
import { 
  Calendar, 
  Plus, 
  Edit, 
  Trash2, 
  DollarSign, 
  Clock,
  TrendingUp,
  TrendingDown
} from 'lucide-react';
import { Schedule } from '../lib/types';
import { schedulingEngine, SchedulingEngine } from '../lib/scheduler/engine';

export function Schedules() {
  const { storage } = useApp();
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingSchedule, setEditingSchedule] = useState<Schedule | null>(null);

  const schedules = storage.getSchedules();
  const accounts = storage.getAccounts();
  const categories = storage.getCategories();

  const handleCreateSchedule = () => {
    setEditingSchedule(null);
    setIsDialogOpen(true);
  };

  const handleEditSchedule = (schedule: Schedule) => {
    setEditingSchedule(schedule);
    setIsDialogOpen(true);
  };

  const handleDeleteSchedule = (scheduleId: string) => {
    if (confirm('Are you sure you want to delete this recurring item?')) {
      storage.deleteSchedule(scheduleId);
    }
  };

  const formatNextDue = (nextDue: string, isOverdue: boolean) => {
    if (!nextDue) return 'Ended';
    
    const date = new Date(nextDue);
    const today = new Date();
    const diffDays = Math.ceil((date.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
    
    if (isOverdue) {
      return `Overdue ${Math.abs(diffDays)} day${Math.abs(diffDays) !== 1 ? 's' : ''}`;
    } else if (diffDays === 0) {
      return 'Due Today';
    } else if (diffDays === 1) {
      return 'Due Tomorrow';
    } else if (diffDays < 7) {
      return `Due in ${diffDays} days`;
    } else {
      return date.toLocaleDateString();
    }
  };

  const getFrequencyDisplay = (spec: string) => {
    if (spec.includes('DAILY')) return 'Daily';
    if (spec.includes('WEEKLY') && spec.includes('INTERVAL=2')) return 'Bi-weekly';
    if (spec.includes('WEEKLY')) return 'Weekly';
    if (spec.includes('MONTHLY') && spec.includes('INTERVAL=3')) return 'Quarterly';
    if (spec.includes('MONTHLY') && spec.includes('INTERVAL=6')) return 'Semi-annually';
    if (spec.includes('MONTHLY')) return 'Monthly';
    if (spec.includes('YEARLY')) return 'Yearly';
    return 'Custom';
  };

  const activeSchedules = schedules.filter(s => s.active);
  const incomeSchedules = activeSchedules.filter(s => s.type === 'income');
  const expenseSchedules = activeSchedules.filter(s => s.type === 'expense');

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold">Recurring Items</h1>
          <p className="text-muted-foreground">
            Manage your recurring income and expenses
          </p>
        </div>
        
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button onClick={handleCreateSchedule}>
              <Plus className="w-4 h-4 mr-2" />
              New Recurring Item
            </Button>
          </DialogTrigger>
          
          <ScheduleDialog
            schedule={editingSchedule}
            accounts={accounts}
            categories={categories}
            onSave={(schedule) => {
              if (editingSchedule) {
                storage.updateSchedule(editingSchedule.id, schedule);
              } else {
                storage.addSchedule(schedule);
              }
              setIsDialogOpen(false);
              setEditingSchedule(null);
            }}
            onCancel={() => {
              setIsDialogOpen(false);
              setEditingSchedule(null);
            }}
          />
        </Dialog>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Income</CardTitle>
            <TrendingUp className="h-4 w-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-green-600">
              {incomeSchedules.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Recurring income sources
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Active Expenses</CardTitle>
            <TrendingDown className="h-4 w-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold text-red-600">
              {expenseSchedules.length}
            </div>
            <p className="text-xs text-muted-foreground">
              Recurring expenses
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Items</CardTitle>
            <Clock className="h-4 w-4 text-blue-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {activeSchedules.length}
            </div>
            <p className="text-xs text-muted-foreground">
              All active schedules
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Schedules List */}
      <Card>
        <CardHeader>
          <CardTitle>All Recurring Items</CardTitle>
        </CardHeader>
        <CardContent>
          {activeSchedules.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="w-12 h-12 mx-auto mb-4 opacity-50" />
              <p>No recurring items yet</p>
              <p className="text-sm">Create your first recurring income or expense to get started</p>
            </div>
          ) : (
            <div className="space-y-4">
              {activeSchedules.map((schedule) => {
                const account = accounts.find(a => a.id === schedule.template.accountId);
                const category = categories.find(c => c.id === schedule.template.categoryId);
                const today = new Date().toISOString().split('T')[0];
                const isOverdue = Boolean(schedule.nextDue && schedule.nextDue < today);
                
                return (
                  <div
                    key={schedule.id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      isOverdue ? 'border-red-200 bg-red-50 dark:border-red-800 dark:bg-red-950' : ''
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <div className={`p-2 rounded-full ${
                          schedule.type === 'income' 
                            ? 'bg-green-100 dark:bg-green-900' 
                            : 'bg-red-100 dark:bg-red-900'
                        }`}>
                          {schedule.type === 'income' ? (
                            <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400" />
                          ) : (
                            <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400" />
                          )}
                        </div>
                        
                        <div>
                          <h3 className="font-medium">
                            {schedule.template.payee || `${schedule.type === 'income' ? 'Income' : 'Expense'} - ${category?.name}`}
                          </h3>
                          <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                            <span>{account?.name}</span>
                            <span>•</span>
                            <span>{category?.name}</span>
                            <span>•</span>
                            <span>{getFrequencyDisplay(schedule.spec)}</span>
                          </div>
                        </div>
                      </div>
                    </div>

                    <div className="flex items-center space-x-4">
                      <div className="text-right">
                        <div className={`text-lg font-semibold ${
                          schedule.type === 'income' ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {schedule.type === 'income' ? '+' : '-'}${Math.abs(schedule.template.amount).toFixed(2)}
                        </div>
                        <div className={`text-xs ${isOverdue ? 'text-red-600' : 'text-muted-foreground'}`}>
                          {formatNextDue(schedule.nextDue || '', isOverdue)}
                        </div>
                      </div>

                      <div className="flex items-center space-x-2">
                        <Badge variant={schedule.autoPost ? 'default' : 'secondary'}>
                          {schedule.autoPost ? 'Auto' : 'Manual'}
                        </Badge>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleEditSchedule(schedule)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleDeleteSchedule(schedule.id)}
                          className="text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Dialog component for creating/editing schedules
interface ScheduleDialogProps {
  schedule: Schedule | null;
  accounts: any[];
  categories: any[];
  onSave: (schedule: Omit<Schedule, 'id'>) => void;
  onCancel: () => void;
}

function ScheduleDialog({ schedule, accounts, categories, onSave, onCancel }: ScheduleDialogProps) {
  const [type, setType] = useState<'income' | 'expense'>(schedule?.type || 'expense');
  const [frequency, setFrequency] = useState('monthly');
  const [startDate, setStartDate] = useState(schedule?.startDate || new Date().toISOString().split('T')[0]);
  const [endDate, setEndDate] = useState(schedule?.endDate || '');
  const [amount, setAmount] = useState(schedule?.template.amount?.toString() || '');
  const [accountId, setAccountId] = useState(schedule?.template.accountId || '');
  const [categoryId, setCategoryId] = useState(schedule?.template.categoryId || '');
  const [payee, setPayee] = useState(schedule?.template.payee || '');
  const [memo, setMemo] = useState(schedule?.template.memo || '');
  const [autoPost, setAutoPost] = useState(schedule?.autoPost ?? false);
  const [dayOfMonth, setDayOfMonth] = useState('1');

  const handleSave = () => {
    if (!amount || !accountId || (type === 'expense' && !categoryId)) {
      return;
    }

    const spec = SchedulingEngine.createRRule(frequency, { dayOfMonth: parseInt(dayOfMonth) });
    const nextDue = schedulingEngine.computeNextDue({
      spec,
      startDate,
      endDate: endDate || undefined
    } as any, startDate);

    const newSchedule: Omit<Schedule, 'id'> = {
      type,
      spec,
      startDate,
      endDate: endDate || undefined,
      template: {
        accountId,
        categoryId: type === 'expense' ? categoryId : undefined,
        payee: payee.trim() || undefined,
        memo: memo.trim() || undefined,
        amount: parseFloat(amount)
      },
      autoPost,
      nextDue,
      active: true
    };

    onSave(newSchedule);
  };

  return (
    <DialogContent className="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{schedule ? 'Edit' : 'New'} Recurring Item</DialogTitle>
        <DialogDescription>
          Set up recurring income or expenses that happen automatically
        </DialogDescription>
      </DialogHeader>

      <div className="space-y-4">
        {/* Type Selection */}
        <div>
          <Label className="text-sm font-medium">Type</Label>
          <Select value={type} onValueChange={(value: 'income' | 'expense') => setType(value)}>
            <SelectTrigger className="mt-1">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="income">Income</SelectItem>
              <SelectItem value="expense">Expense</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Amount and Frequency */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="amount" className="text-sm font-medium">
              Amount *
            </Label>
            <Input
              id="amount"
              type="number"
              step="0.01"
              placeholder="0.00"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label className="text-sm font-medium">Frequency</Label>
            <Select value={frequency} onValueChange={setFrequency}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="weekly">Weekly</SelectItem>
                <SelectItem value="biweekly">Bi-weekly</SelectItem>
                <SelectItem value="monthly">Monthly</SelectItem>
                <SelectItem value="quarterly">Quarterly</SelectItem>
                <SelectItem value="yearly">Yearly</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Day of Month for Monthly/Quarterly/Yearly */}
        {(['monthly', 'quarterly', 'yearly'].includes(frequency)) && (
          <div>
            <Label className="text-sm font-medium">Day of Month</Label>
            <Select value={dayOfMonth} onValueChange={setDayOfMonth}>
              <SelectTrigger className="mt-1">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Array.from({ length: 28 }, (_, i) => i + 1).map(day => (
                  <SelectItem key={day} value={day.toString()}>
                    {day}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Account and Category */}
        <div className="grid grid-cols-1 gap-4">
          <div>
            <Label className="text-sm font-medium">Account *</Label>
            <Select value={accountId} onValueChange={setAccountId}>
              <SelectTrigger className="mt-1">
                <SelectValue placeholder="Select account" />
              </SelectTrigger>
              <SelectContent>
                {accounts.filter(acc => acc.status === 'open').map((account) => (
                  <SelectItem key={account.id} value={account.id}>
                    {account.name} ({account.type})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {type === 'expense' && (
            <div>
              <Label className="text-sm font-medium">Category *</Label>
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {Object.entries(
                    categories.reduce((groups, category) => {
                      const group = category.group || 'Other';
                      if (!groups[group]) groups[group] = [];
                      groups[group].push(category);
                      return groups;
                    }, {} as Record<string, typeof categories>)
                  ).map(([group, groupCategories]) => (
                    <div key={group}>
                      <div className="px-2 py-1.5 text-sm font-semibold text-muted-foreground">
                        {group}
                      </div>
                      {(groupCategories as any[]).map((category: any) => (
                        <SelectItem key={category.id} value={category.id}>
                          {category.name}
                        </SelectItem>
                      ))}
                    </div>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        {/* Payee and Start Date */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label htmlFor="payee" className="text-sm font-medium">
              Payee
            </Label>
            <Input
              id="payee"
              placeholder="Company, person, etc."
              value={payee}
              onChange={(e) => setPayee(e.target.value)}
              className="mt-1"
            />
          </div>

          <div>
            <Label htmlFor="start-date" className="text-sm font-medium">
              Start Date *
            </Label>
            <Input
              id="start-date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="mt-1"
            />
          </div>
        </div>

        {/* Auto-post Toggle */}
        <div className="flex items-center justify-between">
          <div>
            <Label className="text-sm font-medium">Auto-post transactions</Label>
            <p className="text-xs text-muted-foreground">
              Automatically create transactions when due
            </p>
          </div>
          <Switch checked={autoPost} onCheckedChange={setAutoPost} />
        </div>

        {/* Actions */}
        <div className="flex justify-end space-x-2 pt-4">
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave}
            disabled={!amount || !accountId || (type === 'expense' && !categoryId)}
          >
            {schedule ? 'Update' : 'Create'}
          </Button>
        </div>
      </div>
    </DialogContent>
  );
}